import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { GRID_SIZE } from '../lib/constants';
import { getPieceBackgroundPosition } from '../lib/gameUtils';

const PieceOverlayContext = createContext(null);

export function usePieceOverlay() {
  return useContext(PieceOverlayContext);
}

export function PieceOverlayProvider({ children, imageUrl, pieceSize, gridSize = GRID_SIZE, onPenaltyMove }) {
  const [isOverlayActive, setIsOverlayActive] = useState(false);
  const [overlayPiece, setOverlayPiece] = useState(null);
  const [piecePosition, setPiecePosition] = useState({ x: 0, y: 0 });
  const [scaledPieceSize, setScaledPieceSize] = useState(pieceSize);
  const [fullscreenImageSize, setFullscreenImageSize] = useState({ width: 0, height: 0 });
  
  // Reference to track if we're currently dragging
  const isDraggingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  
  // Calculate the scaled piece size based on the fullscreen image size
  const calculateScaledPieceSize = useCallback((imgWidth, imgHeight) => {
    // The piece should be proportionally scaled to the fullscreen image
    // Original: pieceSize corresponds to pieceSize * gridSize = full image
    // Fullscreen: scaledPieceSize should correspond to imgWidth (or imgHeight, assuming square)
    const fullscreenDimension = Math.min(imgWidth, imgHeight);
    const newPieceSize = fullscreenDimension / gridSize;
    setScaledPieceSize(newPieceSize);
    setFullscreenImageSize({ width: imgWidth, height: imgHeight });
    return newPieceSize;
  }, [gridSize]);
  
  // Activate the overlay with a piece
  const activateOverlay = useCallback((piece, clientX, clientY) => {
    setOverlayPiece(piece);
    setPiecePosition({ x: clientX, y: clientY });
    setIsOverlayActive(true);
    isDraggingRef.current = true;
    startPosRef.current = { x: clientX, y: clientY };
    
    // Estimate initial scaled piece size based on viewport
    // The fullscreen image will be max 90vw or 85vh
    const viewportWidth = window.innerWidth * 0.9;
    const viewportHeight = window.innerHeight * 0.85;
    const estimatedImageSize = Math.min(viewportWidth, viewportHeight);
    const estimatedPieceSize = estimatedImageSize / gridSize;
    setScaledPieceSize(estimatedPieceSize);
    
    // Apply penalty move when overlay is used
    if (onPenaltyMove) {
      onPenaltyMove();
    }
  }, [gridSize, onPenaltyMove]);
  
  // Update piece position while dragging
  const updatePiecePosition = useCallback((clientX, clientY) => {
    if (isDraggingRef.current && isOverlayActive) {
      setPiecePosition({ x: clientX, y: clientY });
    }
  }, [isOverlayActive]);
  
  // Deactivate the overlay (when piece is released)
  const deactivateOverlay = useCallback(() => {
    setIsOverlayActive(false);
    setOverlayPiece(null);
    isDraggingRef.current = false;
  }, []);
  
  return (
    <PieceOverlayContext.Provider value={{ 
      isOverlayActive, 
      overlayPiece, 
      piecePosition,
      scaledPieceSize,
      fullscreenImageSize,
      activateOverlay, 
      updatePiecePosition,
      deactivateOverlay,
      calculateScaledPieceSize,
      imageUrl,
      gridSize,
      originalPieceSize: pieceSize
    }}>
      {children}
    </PieceOverlayContext.Provider>
  );
}

// Component that renders the fullscreen overlay with the piece
export function PieceOverlayDisplay() {
  const context = usePieceOverlay();
  const overlayRef = useRef(null);
  const imageRef = useRef(null);
  
  // Extract context values (may be null)
  const {
    isOverlayActive = false,
    overlayPiece = null,
    piecePosition = { x: 0, y: 0 },
    scaledPieceSize = 0,
    updatePiecePosition = () => {},
    deactivateOverlay = () => {},
    calculateScaledPieceSize = () => {},
    imageUrl = '',
    gridSize = 6
  } = context || {};
  
  // Handle mouse/touch move on the overlay
  const handleMove = useCallback((clientX, clientY) => {
    updatePiecePosition(clientX, clientY);
  }, [updatePiecePosition]);
  
  const handleMouseMove = useCallback((e) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);
  
  const handleTouchMove = useCallback((e) => {
    if (e.touches.length > 0) {
      e.preventDefault();
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [handleMove]);
  
  // Handle release
  const handleRelease = useCallback(() => {
    deactivateOverlay();
  }, [deactivateOverlay]);
  
  // Calculate piece size when image loads
  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    calculateScaledPieceSize(img.clientWidth, img.clientHeight);
  }, [calculateScaledPieceSize]);
  
  // Also recalculate on window resize
  useEffect(() => {
    if (!isOverlayActive || !imageRef.current) return;
    
    const handleResize = () => {
      if (imageRef.current) {
        calculateScaledPieceSize(imageRef.current.clientWidth, imageRef.current.clientHeight);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOverlayActive, calculateScaledPieceSize]);
  
  // Early returns after all hooks
  if (!context) return null;
  if (!isOverlayActive || !overlayPiece) return null;
  
  // Calculate background position for the piece (scaled)
  const totalSize = scaledPieceSize * gridSize;
  const bgX = -overlayPiece.correctCol * scaledPieceSize;
  const bgY = -overlayPiece.correctRow * scaledPieceSize;
  const backgroundPosition = `${bgX}px ${bgY}px`;
  
  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
      onMouseMove={handleMouseMove}
      onMouseUp={handleRelease}
      onMouseLeave={handleRelease}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleRelease}
      onTouchCancel={handleRelease}
      style={{ touchAction: 'none' }}
      data-testid="piece-overlay"
    >
      {/* Fullscreen reference image */}
      <img
        ref={imageRef}
        src={imageUrl}
        alt="Reference"
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl pointer-events-none select-none"
        draggable={false}
        onLoad={handleImageLoad}
      />
      
      {/* Instruction text */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-background/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-foreground">
        Move piece to compare • Release to return
      </div>
      
      {/* The dragged piece - follows cursor/touch */}
      <div
        className="fixed pointer-events-none z-[10000]"
        style={{
          left: piecePosition.x - scaledPieceSize / 2,
          top: piecePosition.y - scaledPieceSize / 2,
        }}
      >
        <div
          className="rounded-sm shadow-xl bg-no-repeat"
          style={{
            width: scaledPieceSize,
            height: scaledPieceSize,
            backgroundImage: `url(${overlayPiece.imageUrl})`,
            backgroundPosition,
            backgroundSize: `${totalSize}px ${totalSize}px`,
            transform: `rotate(${overlayPiece.rotation}deg)`,
            border: '3px solid hsl(var(--primary))',
            filter: 'brightness(1.1) contrast(1.05)',
          }}
        />
      </div>
    </div>
  );
}
